import React, { useEffect, useRef, useState } from 'react';
import { hexToRgb } from 'cards/util/hexToRgb';

const recolorImage = (
  canvas: HTMLCanvasElement,
  image: HTMLImageElement,
  hexColor: string
): boolean => {
  const ctx = canvas.getContext('2d');
  if (!ctx) return false;

  const { r, g, b } = hexToRgb(hexColor);
  if (typeof r !== 'number' || typeof g !== 'number' || typeof b !== 'number') {
    // eslint-disable-next-line no-console
    console.log(`Problem recoloring asset - Couldn't convert color "${hexColor}" to RGB`);
    return false;
  }

  // getImageData will throw an error when trying to load images from qa.zola.com via localhost.
  // If that happens, we just won't recolor the image.
  try {
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.drawImage(image, 0, 0, canvas.width, canvas.height);
    const imgData = ctx.getImageData(0, 0, canvas.width, canvas.height);
    const { data } = imgData;

    // Loop over each pixel, recoloring each non-transparent one
    for (let i = 0; i < data.length; i += 4) {
      const alpha = data[i + 3];

      // Only change non-transparent pixels
      if (alpha > 0) {
        data[i + 0] = r;
        data[i + 1] = g;
        data[i + 2] = b;
      }
    }
    ctx.putImageData(imgData, 0, 0);
    return true;
  } catch (error) {
    // eslint-disable-next-line no-console
    console.log(`Problem recoloring asset - Can't render image on canvas`, error);
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    return false;
  }
};

type RecoloredImageProps = {
  src: string;
  color: string;
  width: string;
  height: string;
  className?: string;
};

export const RecoloredImage = (props: RecoloredImageProps): JSX.Element => {
  const { src, color, width, height, className } = props;
  const [loadedImage, setLoadedImage] = useState<HTMLImageElement>();
  const [recolorFailed, setRecolorFailed] = useState(false);
  const canvasRef = useRef<HTMLCanvasElement>(null);

  // Preload the image before using it on the canvas
  useEffect(() => {
    if (!src) return;

    const imageToLoad = new Image();
    imageToLoad.onload = () => {
      // Wait until the image is done loading before setting loadedImage
      setLoadedImage(imageToLoad);
    };
    imageToLoad.onerror = () => {
      // If the image can't be loaded, fall back on the original image
      setRecolorFailed(true);
    };
    imageToLoad.crossOrigin = 'use-credentials';
    imageToLoad.src = src;
  }, [src]);

  // Once all necessary data is available, draw the image to the canvas and recolor it
  useEffect(() => {
    if (!loadedImage || !color || !canvasRef.current) return;
    const succeeded = recolorImage(canvasRef.current, loadedImage, color);
    setRecolorFailed(!succeeded);
  }, [loadedImage, color]);

  return (
    <>
      <canvas ref={canvasRef} width={width} height={height} className={className} />
      {recolorFailed && <img src={src} alt="" draggable="false" className={className} />}
    </>
  );
};
